import React, { useState, useEffect, useCallback } from "react";
import "./SignIn.css";
import GoogleImg from "../../../components/Media/Images/google.png";
import { useNavigate } from "react-router-dom";
import {
  signInWithGoogle,
  getGoogleRedirectIdToken,
  isGoogleSignInCancelled,
} from "../../../services/googleAuth";
import { completeGoogleAuth } from "../../../services/auth.service";
import { useAuth } from "../../../context/AuthContext";
import { Star, Shield, Clock } from "lucide-react";
import Swal from "sweetalert2";
import img from "../../../components/Media/Images/model-with-smokey-eyes-golden-circle-earrings.webp";
import imgLogo from "../../../components/Media/Images/Logo.png";

const showSignInError = (err) => {
  console.error("Google sign-in failed", err);
  Swal.fire({
    icon: "error",
    title: "Sign-in Failed",
    text:
      err?.response?.data?.message ||
      err?.message ||
      "Could not sign in with Google. Please try again.",
    confirmButtonColor: "#c2185b",
  });
};

function SignIn() {
  const navigate = useNavigate();
  const { setAuthenticatedUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const finishSignIn = useCallback(
    async (idToken) => {
      // Backend detects customer or salon based on account
      const authData = await completeGoogleAuth({
        idToken,
        userType: "customer",
      });
      const user = authData?.data?.user;
      setAuthenticatedUser(authData);

      // Navigate based on shop profile state
      const shop = user?.shopProfile || user?.shop;
      if (shop) {
        if (shop.isProfileCompleted && shop.isOnboarded) {
          navigate("/shop/dashboard");
        } else if (shop.isProfileCompleted && !shop.isOnboarded) {
          navigate("/shop/onboard");
        } else {
          navigate("/shop/edit-profile");
        }
      } else {
        // Customer — honour any saved redirect (e.g. from booking flow)
        const redirect = sessionStorage.getItem("redirectAfterLogin");
        if (redirect) {
          sessionStorage.removeItem("redirectAfterLogin");
          navigate(redirect);
        } else {
          navigate("/");
        }
      }
    },
    [navigate, setAuthenticatedUser],
  );

  // Picks up the result when the redirect fallback was used (popup blocked)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const idToken = await getGoogleRedirectIdToken();
        if (!idToken || cancelled) return;
        setLoading(true);
        await finishSignIn(idToken);
      } catch (err) {
        if (!cancelled) showSignInError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [finishSignIn]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const idToken = await signInWithGoogle();
      // null => the page is navigating to Google for the redirect fallback
      if (!idToken) return;
      await finishSignIn(idToken);
    } catch (err) {
      if (!isGoogleSignInCancelled(err)) showSignInError(err);
    }
    setLoading(false);
  };

  return (
    <div className="si-page">
      {/* Left Brand Panel */}
      <div className="si-brand">
        <div className="si-brand-inner">
          <div className="si-logo" onClick={() => navigate("/")}>
            <img src={imgLogo} style={{ width: "50%" }} alt="Logo" />
          </div>

          <div className="si-brand-copy">
            <h2>Your beauty journey starts here</h2>
            <p>
              Sign in to book world-class beauty services, manage appointments,
              and discover premium salons near you.
            </p>
          </div>

          <div className="si-features">
            <div className="si-feature">
              <div className="si-feature-icon">
                <Star size={18} />
              </div>
              <div>
                <strong>Top-Rated Salons</strong>
                <span>Verified professionals, real reviews</span>
              </div>
            </div>
            <div className="si-feature">
              <div className="si-feature-icon">
                <Clock size={18} />
              </div>
              <div>
                <strong>Instant Booking</strong>
                <span>Book in seconds, confirm instantly</span>
              </div>
            </div>
            <div className="si-feature">
              <div className="si-feature-icon">
                <Shield size={18} />
              </div>
              <div>
                <strong>Secure & Private</strong>
                <span>Your data is always protected</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="si-form-panel">
        <div className="si-form-card">
          <div className="si-form-header">
            <h1>Welcome back</h1>
            <p>Sign in to your Orucom account</p>
          </div>

          <button
            className="si-google-btn"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <img src={GoogleImg} alt="Google" />
            {loading ? "Signing in…" : "Continue with Google"}
          </button>

          <p className="si-signup-link">
            Don't have an account?{" "}
            <button onClick={() => navigate("/signup")}>Create one free</button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignIn;
